import React, { useEffect, useRef } from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Globe, Monitor } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  saveLastLocale,
  getLastLocale,
  saveLastChannel,
  getLastChannel,
} from '@/lib/attributePreferences';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { useProductDetailPageTranslations } from '@/hooks/useProductDetailPageTranslations';

interface LocaleChannelSelectorProps {
  selectedLocale: string;
  selectedChannel: string;
  onLocaleChange: (locale: string) => void;
  onChannelChange: (channel: string) => void;
  disableLocalePersistence?: boolean;
  disableChannelPersistence?: boolean;
}

/**
 * Component for selecting locale and channel in the attributes tab
 */
const LocaleChannelSelector: React.FC<LocaleChannelSelectorProps> = ({
  selectedLocale,
  selectedChannel,
  onLocaleChange,
  onChannelChange,
  disableLocalePersistence = false,
  disableChannelPersistence = false,
}) => {
  const { locales, channels, defaultLocale, defaultChannel } = useOrgSettings();
  const initializedRef = useRef(false);

  // On component mount, check localStorage for saved preferences and validate they exist
  useEffect(() => {
    // Only run once when organization data is loaded
    if (initializedRef.current || locales.length === 0 || channels.length === 0) {
      return;
    }

    let shouldUpdate = false;

    if (!disableLocalePersistence) {
      const savedLocale = getLastLocale(defaultLocale);
      // Validate that the saved locale exists in the current organization
      const localeExists = locales.some(l => l.code === savedLocale);
      const validLocale = localeExists ? savedLocale : defaultLocale;

      if (validLocale && validLocale !== selectedLocale) {
        onLocaleChange(validLocale);
        shouldUpdate = true;
        // Update localStorage if we had to fall back to default
        if (!localeExists && validLocale !== savedLocale) {
          saveLastLocale(validLocale);
        }
      }
    }

    if (!disableChannelPersistence) {
      const savedChannel = getLastChannel(defaultChannel?.code || '');
      // Validate that the saved channel exists in the current organization
      const channelExists = channels.some(c => c.code === savedChannel);
      const validChannel = channelExists ? savedChannel : defaultChannel?.code || '';

      if (validChannel && validChannel !== selectedChannel) {
        onChannelChange(validChannel);
        shouldUpdate = true;
        // Update localStorage if we had to fall back to default
        if (!channelExists && validChannel !== savedChannel) {
          saveLastChannel(validChannel);
        }
      }
    }

    if (shouldUpdate) {
      initializedRef.current = true;
    }
  }, [
    locales.length,
    channels.length,
    defaultLocale,
    defaultChannel?.code,
    disableLocalePersistence,
    disableChannelPersistence,
    // Note: Removed selectedLocale, selectedChannel, onLocaleChange, onChannelChange to prevent infinite loops
  ]);

  // Save preferences when they change
  const handleLocaleChange = (locale: string) => {
    onLocaleChange(locale);
    if (!disableLocalePersistence) {
      saveLastLocale(locale);
    }
  };

  const handleChannelChange = (channel: string) => {
    onChannelChange(channel);
    if (!disableChannelPersistence) {
      saveLastChannel(channel);
    }
  };

  // Find the current locale and channel labels
  const localeLabel = locales.find(l => l.code === selectedLocale)?.label || selectedLocale;
  const channelLabel = channels.find(c => c.code === selectedChannel)?.label || selectedChannel;

  // Access the translation labels
  const { getAttributesLabels } = useProductDetailPageTranslations();
  const localeText = getAttributesLabels.localeChannelSelector;

  return (
    <div className="flex items-center space-x-4 rounded-md border border-border bg-muted/30 p-3 dark:bg-muted/20">
      <div className="flex items-center space-x-2">
        <Badge
          variant="outline"
          className="border-blue-200 bg-blue-50 px-2 text-xs text-blue-700 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
        >
          <Globe className="mr-1.5 size-3.5" />
          {localeText.localeLabel}
        </Badge>
        <Select value={selectedLocale} onValueChange={handleLocaleChange}>
          <SelectTrigger className="w-[150px] bg-background">
            <SelectValue placeholder={localeText.localeChangeTooltip}>{localeLabel}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>{localeText.localeLabel}</SelectLabel>
              {locales.map(locale => (
                <SelectItem key={locale.code} value={locale.code}>
                  {locale.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center space-x-2">
        <Badge
          variant="outline"
          className="border-purple-200 bg-purple-50 px-2 text-xs text-purple-700 dark:border-purple-800 dark:bg-purple-900/30 dark:text-purple-300"
        >
          <Monitor className="mr-1.5 size-3.5" />
          {localeText.channelLabel}
        </Badge>
        <Select value={selectedChannel} onValueChange={handleChannelChange}>
          <SelectTrigger className="w-[150px] bg-background">
            <SelectValue placeholder={localeText.channelChangeTooltip}>{channelLabel}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>{localeText.channelLabel}</SelectLabel>
              {channels.map(channel => (
                <SelectItem key={channel.code} value={channel.code}>
                  {channel.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default React.memo(LocaleChannelSelector);
